import type { CardBody, DriverBillingBody, DriverBody, PublicDriverBody } from '@pulsepoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, del, get, patch, post } from './client';

export interface Driver {
  id: string;
  organisationId: string;
  organisationName: string;
  name: string;
  email: string;
  phone: string | null;
  postcode: string | null;
  onboardedOn: string | null;
  vehicleType: string | null;
  registration: string | null;
  /** Powersensor is on for their organisation. */
  powersensorEnabled: boolean;
  powersensorEmail: string | null;
  evSensorNumber: number;
  serialWifiPlug: string | null;
  serialSensorDistribution: string | null;
  serialSensorCharger: string | null;
  notes: string | null;
  needsReview: boolean;
  isActive: boolean;
  createdAt: string;
  cardCount: number;
  chargerCount: number;
  lastSessionAt: string | null;
}

export interface Card {
  id: string;
  driverId: string;
  driverName: string;
  tag: string;
  label: string | null;
  isActive: boolean;
  expiresOn: string | null;
  isExpired: boolean;
  /** Its own $/kWh instead of the charger's private price; null: the private price. */
  ratePerKwh: number | null;
  createdAt: string;
  lastUsedAt: string | null;
  sessionCount: number;
}

export interface DriverBilling {
  isBillable: boolean;
  dayOfMonth: number;
  stripeCustomerId: string | null;
  stripeCustomerEmail: string | null;
  lastBilledYear: number | null;
  lastBilledMonth: number | null;
}

export interface DriverDetail extends Driver {
  cards: Card[];
  odometer: { id: string; km: number; recordedAt: string }[];
  /** Missing when you may not see billing. */
  billing?: DriverBilling | null;
  chargers: { id: string; chargePointId: string; label: string | null; siteName: string | null }[];
  onboardingLink: { label: string | null; createdAt: string } | null;
}

export interface OnboardingLink {
  id: string;
  organisationId: string;
  organisationName: string;
  label: string | null;
  expiresAt: string;
  revokedAt: string | null;
  submissionCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  isLive: boolean;
  /** Only when it's just been made. */
  url?: string;
}

export interface UnknownCard {
  tag: string;
  lastSeenAt: string;
  times: number;
  charger: string;
  siteName: string | null;
}

export type DriverShow = 'active' | 'review' | 'inactive' | 'all';

export const driversKey = ['drivers'] as const;
const linksKey = ['onboarding-links'] as const;

export const useDrivers = (show: DriverShow = 'all', organisationId?: string, enabled = true) =>
  useQuery({
    queryKey: [...driversKey, 'list', show, organisationId ?? 'all'],
    queryFn: () => get<Driver[]>(`/drivers?show=${show}${organisationId ? `&organisationId=${organisationId}` : ''}`),
    enabled,
  });

export const useDriver = (id: string | undefined) =>
  useQuery({ queryKey: [...driversKey, 'one', id], queryFn: () => get<DriverDetail>(`/drivers/${id}`), enabled: !!id });

export const useUnknownCards = (organisationId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['cards', 'unknown', organisationId ?? 'all'],
    queryFn: () => get<UnknownCard[]>(`/cards/unknown${organisationId ? `?organisationId=${organisationId}` : ''}`),
    enabled,
    staleTime: 30_000,
  });

/** Every change returns the driver as it is now: put it in the cache and refresh the lists. */
function useDriverChange<V, R extends DriverDetail | void = DriverDetail>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (d) => {
      if (d) qc.setQueryData([...driversKey, 'one', d.id], d);
      void qc.invalidateQueries({ queryKey: [...driversKey, 'list'] });
      void qc.invalidateQueries({ queryKey: ['organisations'] }); // driver counts
      void qc.invalidateQueries({ queryKey: ['cards', 'unknown'] });
    },
  });
}

type CreateBody = DriverBody & { cards?: CardBody[] };
export const useCreateDriver = () => useDriverChange((body: CreateBody) => post<DriverDetail>('/drivers', body));
export const useUpdateDriver = () =>
  useDriverChange(({ id, ...body }: Partial<DriverBody> & { id: string; isActive?: boolean }) => patch<DriverDetail>(`/drivers/${id}`, body));
export const useDeactivateDriver = () => useDriverChange((id: string) => del<DriverDetail>(`/drivers/${id}`));
export const useApproveDriver = () => useDriverChange((id: string) => post<DriverDetail>(`/drivers/${id}/approve`));
export const useRejectDriver = () => useDriverChange(async (id: string) => void (await post(`/drivers/${id}/reject`)));
export const useDriverBilling = () =>
  useDriverChange(({ id, ...body }: DriverBillingBody & { id: string }) => api<DriverDetail>('PUT', `/drivers/${id}/billing`, body));
export const useAddOdometer = () =>
  useDriverChange(({ id, ...body }: { id: string; km: number | string; recordedOn: string }) => post<DriverDetail>(`/drivers/${id}/odometer`, body));
export const useDeleteOdometer = () =>
  useDriverChange(({ id, logId }: { id: string; logId: string }) => del<DriverDetail>(`/drivers/${id}/odometer/${logId}`));
export const useAddCard = () => useDriverChange(({ driverId, ...body }: CardBody & { driverId: string }) => post<DriverDetail>(`/drivers/${driverId}/cards`, body));

/** Card changes return the card; the driver is fetched again. */
function useCardChange<V>(fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: driversKey });
    },
  });
}
export const useUpdateCard = () =>
  useCardChange(({ id, ...body }: { id: string; label?: string | null; isActive?: boolean; expiresOn?: string | null }) => patch<Card>(`/cards/${id}`, body));
export const useDeleteCard = () => useCardChange((id: string) => del(`/cards/${id}`));

export const useOnboardingLinks = (enabled = true) =>
  useQuery({ queryKey: linksKey, queryFn: () => get<OnboardingLink[]>('/drivers/onboarding-links'), enabled });

export function useCreateOnboardingLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { organisationId: string; label?: string; days: number }) => post<OnboardingLink>('/drivers/onboarding-links', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: linksKey }),
  });
}

export function useRevokeOnboardingLink() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => del(`/drivers/onboarding-links/${id}`), onSuccess: () => void qc.invalidateQueries({ queryKey: linksKey }) });
}

// The public sign-up page (no sign-in)
export const useOnboarding = (token: string) =>
  useQuery({ queryKey: ['onboarding', token], queryFn: () => get<{ organisationName: string; expiresAt: string }>(`/public/onboarding/${token}`), retry: false });
export const useSubmitOnboarding = (token: string) => useMutation({ mutationFn: (body: PublicDriverBody) => post<{ ok: true }>(`/public/onboarding/${token}`, body) });

/** Cards that can charge now, in one organisation (to start a session on one). */
export const useUsableCards = (organisationId: string | null | undefined) =>
  useQuery({ queryKey: [...driversKey, 'cards', organisationId], queryFn: () => get<Card[]>(`/cards?organisationId=${organisationId}`), enabled: !!organisationId });
